import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Crown, Check, CheckCircle2, XCircle, Megaphone, Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { formatEur, formatDate } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const isProActive = (u) => u?.pro_subscription?.status === "active" && new Date(u.pro_subscription.current_period_end) > new Date();
const PROMO_KEYS = ["highlight_3d", "category_showcase_7d", "top_7d"];

export const useServices = (enabled = true) => useQuery({ queryKey: ["services"], enabled, queryFn: async () => (await api.get("/services")).data });

export const buyService = async (service_key, listing_id) => {
  const { data } = await api.post("/services/checkout", { service_key, listing_id, origin_url: window.location.origin });
  window.location.href = data.checkout_url;
};

export const ServicePicker = ({ value, onChange, withNone }) => {
  const { t, locale } = useI18n();
  const { data } = useServices();
  const opts = (data?.services || []).filter((s) => PROMO_KEYS.includes(s.key));
  return (
    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4" data-testid="service-picker">
      {withNone && <button type="button" onClick={() => onChange("")} className={`rounded-2xl border p-3 text-left text-sm ${!value ? "border-[#3B1272] bg-[#EFE9FB]" : "border-[#E2E8F0]"}`} data-testid="service-option-none">{t("p4.none")}</button>}
      {opts.map((s) => (
        <button type="button" key={s.key} onClick={() => onChange(s.key)} data-testid={`service-option-${s.key}`}
          className={`rounded-2xl border p-3 text-left ${value === s.key ? "border-[#3B1272] bg-[#EFE9FB]" : "border-[#E2E8F0] hover:border-[#C9B8EC]"}`}>
          <p className="flex items-center gap-1.5 text-sm font-semibold"><Megaphone className="h-4 w-4 text-[#FF7A6B]" />{s.label}</p>
          <p className="mt-1 font-display text-lg font-bold text-[#3B1272]">{formatEur(s.final_price_cents, locale)}{s.discounted && <s className="ml-2 text-xs font-normal text-[#94A3B8]">{formatEur(s.price_cents, locale)}</s>}</p>
          {s.discounted && <p className="text-[11px] font-semibold text-emerald-700">{t("p4.proDiscount")}</p>}
        </button>
      ))}
    </div>
  );
};

export const PromoteDialog = ({ listingId, onClose }) => {
  const { t } = useI18n();
  const [key, setKey] = useState("highlight_3d");
  const [busy, setBusy] = useState(false);
  const pay = async () => { setBusy(true); try { await buyService(key, listingId); } catch (e) { toast.error(errMsg(e)); setBusy(false); } };
  return (
    <Dialog open={!!listingId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl rounded-3xl" data-testid="promote-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("p4.promoteTitle")}</DialogTitle></DialogHeader>
        <ServicePicker value={key} onChange={setKey} />
        <p className="text-xs text-[#64748B]">{t("p4.promoteNote")}</p>
        <button onClick={pay} disabled={busy || !key} className="btn-coral w-full" data-testid="promote-pay-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("p4.payService")}</button>
        <p className="text-center text-[11px] text-[#64748B]">{t("p3.testNote")}</p>
      </DialogContent>
    </Dialog>
  );
};

const AiPacks = () => {
  const { t, locale } = useI18n();
  const { data: c } = useQuery({ queryKey: ["ai-credits"], queryFn: async () => (await api.get("/ai/credits")).data });
  const [busy, setBusy] = useState(false);
  const buy = async () => { setBusy(true); try { await buyService("ai_pack_20", null); } catch (e) { toast.error(errMsg(e)); setBusy(false); } };
  if (!c) return null;
  return (
    <div className="grid gap-5 rounded-3xl border border-[#EEF0F6] bg-white p-7 lg:grid-cols-[1fr_auto]" data-testid="ai-packs">
      <div>
        <p className="flex items-center gap-2 font-display text-xl font-bold text-[#3B1272]"><Sparkles className="h-5 w-5 text-[#FF7A6B]" />{t("p5.ai.title")}</p>
        <p className="mt-1 text-sm text-[#4B4763]">{t("p5.ai.rules")}</p>
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <span className="rounded-full bg-[#EFE9FB] px-3 py-1.5 font-semibold text-[#3B1272]" data-testid="ai-total-left">{t("p5.ai.left")}: {c.total_left}</span>
          {c.pro_active && <span className="rounded-full bg-emerald-50 px-3 py-1.5 font-semibold text-emerald-700" data-testid="ai-pro-free-left">{t("p5.ai.freePro")}: {c.pro_free_left}/{c.pro_free_limit}</span>}
        </div>
        {c.packs.length > 0 && <ul className="mt-3 space-y-1 text-xs text-[#64748B]" data-testid="ai-packs-list">{c.packs.map((p) => <li key={p.pack_id}>{p.remaining}/{p.total} · {t("p5.ai.expires")} {formatDate(p.expires_at, locale)}</li>)}</ul>}
      </div>
      <div className="flex flex-col justify-center gap-2 lg:min-w-[240px]">
        <p className="font-display text-2xl font-bold text-[#1E1B2E]">{formatEur(c.pack_price_cents, locale)} <span className="text-sm font-normal text-[#64748B]">/ {c.pack_size} {t("p5.ai.requests")}</span></p>
        <button onClick={buy} disabled={busy} className="btn-coral" data-testid="ai-pack-buy-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("p5.ai.buy")}</button>
        <p className="text-[11px] text-[#64748B]">{t("p5.ai.validity")}</p>
      </div>
    </div>
  );
};

export const PaymentsTab = () => {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const { data, refetch } = useServices();
  const { data: history = [] } = useQuery({ queryKey: ["svc-history"], queryFn: async () => (await api.get("/services/history")).data });
  const [busy, setBusy] = useState(false);
  const sub = data?.pro_subscription;
  const pro = data?.services?.find((s) => s.key === "pro_monthly");
  const subscribe = async () => { setBusy(true); try { await buyService("pro_monthly", null); } catch (e) { toast.error(errMsg(e)); setBusy(false); } };
  const cancel = async () => { try { await api.post("/services/pro/cancel"); toast.success("OK"); refetch(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-6" data-testid="payments-tab">
      <div className="grid gap-5 rounded-3xl bg-gradient-to-br from-[#3B1272] to-[#581C9C] p-7 text-white lg:grid-cols-[1fr_auto]">
        <div>
          <p className="flex items-center gap-2 font-display text-2xl font-bold"><Crown className="h-6 w-6 text-amber-300" />{t("p4.pay.proTitle")}</p>
          <p className="mt-1 text-white/80">{pro ? `${formatEur(pro.price_cents, locale)} / mese` : t("p4.pay.proPrice")}</p>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">{t("p4.pay.proBenefits").map((b) => <li key={b} className="flex gap-2 text-sm"><Check className="h-4 w-4 shrink-0 text-amber-300" />{b}</li>)}</ul>
        </div>
        <div className="flex flex-col justify-center gap-2 lg:min-w-[240px]">
          {data?.pro_active ? <>
            <p className="text-sm" data-testid="pro-status">{sub.cancel_at_period_end ? t("p4.pay.cancelling") : t("p4.pay.active")} {formatDate(sub.current_period_end, locale)}</p>
            {!sub.cancel_at_period_end && <button onClick={cancel} className="rounded-full bg-white/15 px-4 py-2 text-sm font-semibold" data-testid="pro-cancel-btn">{t("p4.pay.cancel")}</button>}
          </> : user.role === "pro" ? <button onClick={subscribe} disabled={busy} className="btn-coral" data-testid="pro-subscribe-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("p4.pay.subscribe")}</button>
            : <p className="text-sm text-amber-200" data-testid="pro-business-only">{t("p4.pay.businessOnly")}</p>}
        </div>
      </div>
      <AiPacks />
      <div>
        <p className="mb-3 font-display font-bold">{t("p4.pay.history")}</p>
        {!history.length ? <p className="rounded-2xl bg-[#F8F9FC] px-5 py-8 text-center text-sm text-[#64748B]">{t("p4.pay.empty")}</p> : (
          <div className="divide-y divide-[#EEF0F6] rounded-2xl border border-[#EEF0F6]" data-testid="service-history">
            {history.map((h) => <div key={h.service_order_id} className="flex flex-wrap justify-between gap-2 px-4 py-3 text-sm"><span>{data?.services?.find((s) => s.key === h.service_key)?.label || h.service_key}{h.is_test && <span className="pill ml-2 bg-[#1E1B2E] text-white">TEST</span>}</span><span>{formatEur(h.amount_cents, locale)} · {h.status} · {formatDate(h.created_at, locale)}</span></div>)}
          </div>
        )}
      </div>
    </div>
  );
};

export const ServiceResult = () => {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const { data } = useQuery({ queryKey: ["svc-status", params.get("session_id")], refetchInterval: (q) => (q.state.data?.status === "pending_payment" ? 2000 : false),
    queryFn: async () => (await api.get(`/services/status?session_id=${params.get("session_id")}`)).data });
  const ok = data?.status === "paid";
  return (
    <div className="mx-auto max-w-lg px-4 py-20 text-center" data-testid={ok ? "service-success" : "service-pending"}>
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#EFE9FB]">{!data || data.status === "pending_payment" ? <Loader2 className="h-8 w-8 animate-spin text-[#3B1272]" /> : ok ? <CheckCircle2 className="h-8 w-8 text-emerald-600" /> : <XCircle className="h-8 w-8 text-[#E0533F]" />}</div>
      <h1 className="mt-5 text-3xl font-bold">{!data || data.status === "pending_payment" ? t("p4.result.wait") : ok ? t("p4.result.ok") : t("p4.result.ko")}</h1>
      {ok && <p className="mt-3 text-[#4B4763]">{t("p4.result.okText")}</p>}
      <Link to={["pro_monthly", "ai_pack_20"].includes(data?.service_key) ? "/account?tab=payments" : "/account?tab=listings"} className="btn-violet mt-8 inline-flex" data-testid="service-result-btn">{t("account.title")}</Link>
    </div>
  );
};
